import React, { useEffect, useState, useRef, useCallback } from "react";
import Card from "../ui/Card";
import { useAccountMapping, useAccounts, useUpdateAccountMapping } from "@/hooks/use-account-mapping";
import { LoadingSpinner } from "../LoadingSpinner";
import { Divider, IconButton, TextField, InputAdornment, Checkbox } from "@mui/material";
import { Plus, X, Trash2, SquarePen, ChevronsUpDown, ChevronsDownUp } from "lucide-react";
import { CreateEditAccountGroupDialog } from "./CreateEditAccountGroupDialog";
import { AccountGroup, AccountMapping } from "@/types/account-mapping-api";
import AccountCard from "./AccountCard";
import { 
  draggable, 
  dropTargetForElements, 
  monitorForElements 
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { ConfirmDialog } from "../common/ConfirmDialog";

interface EditAccountMappingProps {
  clientId: string;
  accountMappingId: string | null;
  onMappingChanged?: (editedMapping: AccountMapping | null) => void;
}

const EditAccountMapping: React.FC<EditAccountMappingProps> = ({
  clientId,
  accountMappingId,
  onMappingChanged,
}) => {
  // state
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState<boolean>(false);
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState<boolean>(false);
  const [highlightedGroupId, setHighlightedGroupId] = useState<string | null>(null);
  const [isAccountListHighlighted, setIsAccountListHighlighted] = useState<boolean>(false);
  const [localAccountMapping, setLocalAccountMapping] = useState<AccountMapping | null>(null);
  const [accountFilter, setAccountFilter] = useState<string>("");
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());
  const [selectedGroup, setSelectedGroup] = useState<AccountGroup | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Track cleanup functions for drag and drop elements
  const dragCleanupFunctions = useRef<Map<string, () => void>>(new Map());
  const dropCleanupFunctions = useRef<Map<string, () => void>>(new Map());
  const accountListDropCleanup = useRef<(() => void) | null>(null);

  // hooks
  const { selectedAccountMapping, isLoadingAccountMapping, accountMappingError, } = useAccountMapping(accountMappingId!);

  // Notify parent component about changes with the actual mapping
  useEffect(() => {
    if (onMappingChanged && localAccountMapping) {
      onMappingChanged(localAccountMapping);
    }
  }, [localAccountMapping, onMappingChanged]);

  // Filter function to get filtered accounts
  const getFilteredAccounts = useCallback(() => {
    if (!localAccountMapping) return [];
    return localAccountMapping.accounts.filter((x) => !x.groupId && x.number.startsWith(accountFilter));
  }, [localAccountMapping, accountFilter]);

  // handlers
  const handleCreateAccountGroup = async (name: string) => {
    const group: AccountGroup = {
      // new guid
      id: crypto.randomUUID(),
      name: name,
    };
    const updatedMapping = {
      ...localAccountMapping,
      groups: [...localAccountMapping.groups, group]
    };
    setLocalAccountMapping(updatedMapping);
  };

  const handleDeleteAccountGroup = async (groupId: string) => {
    // Remove group from localAccountMapping
    const updatedMapping = {
      ...localAccountMapping,
      groups: localAccountMapping!.groups.filter((g) => g.id !== groupId),
      accounts: localAccountMapping!.accounts.map((account) => {
        if (account.groupId === groupId) {
          return { ...account, groupId: null }; // Remove groupId from account
        }
        return account;
      })
    };
    setLocalAccountMapping(updatedMapping);
  };

  const handleEditAccountGroup = async (group: AccountGroup) => {
    // find index of group in localAccountMapping.groups
    const groupIndex = localAccountMapping?.groups.findIndex((g) => g.id === group.id);
    // replace this group with the new group
    if (groupIndex !== undefined && groupIndex >= 0) {
      const updatedGroups = [...localAccountMapping!.groups];
      updatedGroups[groupIndex] = group;
      const updatedMapping = {
        ...localAccountMapping,
        groups: updatedGroups
      };
      setLocalAccountMapping(updatedMapping);
    }
    setSelectedGroup(null);
  }

  // Function to handle account drop into a group
  const assignAccountToGroup = useCallback((accountNumbers: string[], groupId: string | null) => {
    const updatedAccounts = localAccountMapping.accounts.map(account => {
      if (accountNumbers.includes(account.number)) {
        return { ...account, groupId };
      }
      return account;
    });
    
    // Update local state with the new changes
    const updatedMapping = {
      ...localAccountMapping,
      accounts: updatedAccounts
    };
    
    setLocalAccountMapping(updatedMapping);
  }, [localAccountMapping]);

  useEffect(() => {
    if (selectedAccountMapping) {
      setLocalAccountMapping(selectedAccountMapping);
    }
  }, [selectedAccountMapping]);

  // Expand empty groups by default when data loads
  useEffect(() => {
    if (localAccountMapping) {
      const emptyGroupIds = localAccountMapping.groups
        .filter(group => !localAccountMapping.accounts.some(account => account.groupId === group.id))
        .map(group => group.id);
      
      setExpandedGroups(prev => {
        const newSet = new Set(prev);
        emptyGroupIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  }, [localAccountMapping]);

  // Toggle group expansion
  const toggleGroupExpansion = useCallback((groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  }, []);

  // Setup drag and drop for accounts
  const setupDragAndDrop = useCallback((accountEl: HTMLElement, accountNumber: string) => {
    // Clean up previous registration if it exists
    if (dragCleanupFunctions.current.has(accountNumber)) {
      dragCleanupFunctions.current.get(accountNumber)?.();
      dragCleanupFunctions.current.delete(accountNumber);
    }
    
    // Register new draggable
    const cleanup = draggable({
      element: accountEl,
      onGenerateDragPreview: () => false, // Prevent automatic preview generation
      onDragStart: (start) => {
        // Only start drag if it's not a click on the selection area
        const target = start.source.element.querySelector('.account-selection-area') || 
                      (start.source.element as HTMLElement).closest('.account-selection-area');
        if (target) {
          return false;
        }
        
        // Determine which accounts we're dragging
        const accountsToMove = selectedAccounts.has(accountNumber) 
          ? Array.from(selectedAccounts)
          : [accountNumber];
        
        // Add visual feedback for all accounts being dragged
        accountsToMove.forEach(accNum => {
          const accountElements = document.querySelectorAll(`[data-account-number="${accNum}"]`);
          accountElements.forEach(el => {
            el.classList.add('opacity-50', 'ring-2', 'ring-blue-500', 'rounded-md');
          });
        });
        
        return true;
      },
      onDrop: () => {
        // Remove visual effects
        document.querySelectorAll('.opacity-50.ring-2.ring-blue-500').forEach(el => {
          el.classList.remove('opacity-50', 'ring-2', 'ring-blue-500', 'rounded-md');
        });
      },
      getInitialData: () => {
        // If this account is selected, move all selected accounts
        // Otherwise, just move this single account
        const accountsToMove = selectedAccounts.has(accountNumber) 
          ? Array.from(selectedAccounts)
          : [accountNumber];
        
        return { 
          accountNumber,
          selectedAccounts: accountsToMove
        };
      }
    });
    
    // Store cleanup function
    dragCleanupFunctions.current.set(accountNumber, cleanup);
  }, [selectedAccounts]);

  // Handle card click to select/deselect
  const handleCardClick = useCallback((accountNumber: string, event: React.MouseEvent) => {
    // If clicking on checkbox area, let the checkbox handle it
    if ((event.target as HTMLElement).closest('.account-selection-area')) {
      return;
    }
    
    setSelectedAccounts(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(accountNumber)) {
        newSelection.delete(accountNumber);
      } else {
        newSelection.add(accountNumber);
      }
      return newSelection;
    });
  }, []);

  // Toggle account selection
  const handleAccountSelection = useCallback((accountNumber: string, isSelected: boolean) => {
    setSelectedAccounts(prev => {
      const newSelection = new Set(prev);
      if (isSelected) {
        newSelection.add(accountNumber);
      } else {
        newSelection.delete(accountNumber);
      }
      return newSelection;
    });
  }, []);

  // Handle "Check all filtered accounts" checkbox
  const handleCheckAllFiltered = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const filteredAccounts = getFilteredAccounts().map(a => a.number);
    
    // Count how many filtered accounts are currently selected
    const selectedFilteredAccounts = filteredAccounts.filter(num => selectedAccounts.has(num));
    
    // If all filtered accounts are already selected, unselect them all
    // Otherwise, select all filtered accounts
    setSelectedAccounts(prev => {
      const newSelection = new Set(prev);
      
      if (selectedFilteredAccounts.length === filteredAccounts.length) {
        // Remove all filtered accounts from selection
        filteredAccounts.forEach(num => newSelection.delete(num));
      } else {
        // Add all filtered accounts to selection
        filteredAccounts.forEach(num => newSelection.add(num));
      }
      
      return newSelection;
    });
  }, [getFilteredAccounts, selectedAccounts]);

  // Setup drop targets for groups
  const setupDropTarget = useCallback((groupEl: HTMLElement, groupId: string) => {
    // Clean up previous registration if it exists
    if (dropCleanupFunctions.current.has(groupId)) {
      dropCleanupFunctions.current.get(groupId)?.();
      dropCleanupFunctions.current.delete(groupId);
    }
    
    // Register new drop target
    const cleanup = combine(
      dropTargetForElements({
        element: groupEl,
        getData: () => ({ groupId }),
        onDragEnter: () => {
          setHighlightedGroupId(groupId);
        },
        onDragLeave: () => {
          setHighlightedGroupId(null);
        },
        onDrop: ({ source }) => {
          // Clear visual effects
          document.querySelectorAll('.opacity-50.ring-2.ring-blue-500').forEach(el => {
            el.classList.remove('opacity-50', 'ring-2', 'ring-blue-500');
          });
          
          const accountNumbers = source.data?.selectedAccounts || [];
          if (Array.isArray(accountNumbers) && accountNumbers.length > 0) {
            // Process all selected accounts
            assignAccountToGroup(accountNumbers, groupId);
            
            // Only clear selection if we moved one of the selected accounts
            const hasSelectedAccountMoved = accountNumbers.some(accNum => selectedAccounts.has(accNum));
            if (hasSelectedAccountMoved) {
              setSelectedAccounts(new Set());
            }
          } else if (typeof source.data?.accountNumber === 'string') {
            assignAccountToGroup([source.data.accountNumber], groupId);
            
            // Clear selection if the moved account was selected
            if (selectedAccounts.has(source.data.accountNumber)) {
              setSelectedAccounts(new Set());
            }
          }
          setHighlightedGroupId(null);
        }
      }),
      monitorForElements({
        onDrop: () => {
          setHighlightedGroupId(null);
          // Clear visual effects
          document.querySelectorAll('.opacity-50.ring-2.ring-blue-500').forEach(el => {
            el.classList.remove('opacity-50', 'ring-2', 'ring-blue-500');
          });
        }
      })
    );
    
    // Store cleanup function
    dropCleanupFunctions.current.set(groupId, cleanup);
  }, [assignAccountToGroup, selectedAccounts]);

  // Setup drop target for the account list
  const setupAccountListDropTarget = useCallback((element: HTMLElement) => {
    // Clean up previous registration if it exists
    if (accountListDropCleanup.current) {
      accountListDropCleanup.current();
      accountListDropCleanup.current = null;
    }
    
    // Register new drop target for account list
    const cleanup = combine(
      dropTargetForElements({
        element,
        getData: () => ({ isAccountList: true }),
        onDragEnter: () => {
          setIsAccountListHighlighted(true);
        },
        onDragLeave: () => {
          setIsAccountListHighlighted(false);
        },
        onDrop: ({ source }) => {
          // Clear visual effects
          document.querySelectorAll('.opacity-50.ring-2.ring-blue-500').forEach(el => {
            el.classList.remove('opacity-50', 'ring-2', 'ring-blue-500');
          });
          
          const accountNumbers = source.data?.selectedAccounts || [];
          if (Array.isArray(accountNumbers) && accountNumbers.length > 0) {
            assignAccountToGroup(accountNumbers, null);
          } else if (typeof source.data?.accountNumber === 'string') {
            assignAccountToGroup([source.data.accountNumber], null);
          }
          setIsAccountListHighlighted(false);
          // Clear selection after drop
          setSelectedAccounts(new Set());
        }
      }),
      monitorForElements({
        onDrop: () => {
          setIsAccountListHighlighted(false);
          // Clear visual effects
          document.querySelectorAll('.opacity-50.ring-2.ring-blue-500').forEach(el => {
            el.classList.remove('opacity-50', 'ring-2', 'ring-blue-500');
          });
        }
      })
    );
    
    // Store cleanup function
    accountListDropCleanup.current = cleanup;
  }, [assignAccountToGroup]);

  // Cleanup drag and drop listeners when component unmounts
  useEffect(() => {
    return () => {
      // Clean up all draggable elements
      dragCleanupFunctions.current.forEach(cleanup => cleanup());
      dragCleanupFunctions.current.clear();
      
      // Clean up all drop targets
      dropCleanupFunctions.current.forEach(cleanup => cleanup());
      dropCleanupFunctions.current.clear();
      
      // Clean up account list drop target
      if (accountListDropCleanup.current) {
        accountListDropCleanup.current();
        accountListDropCleanup.current = null;
      }
    };
  }, []);

  if (isLoadingAccountMapping || !localAccountMapping) {
    return <LoadingSpinner />;
  }

  return (
    <div className="h-screen flex flex-col">
      <div className="flex gap-4 pb-4 h-[85%]">
      <Card
          hoverable={false}
          className={`flex-grow border border-palette-dark-gray rounded-lg flex flex-col h-full ${isAccountListHighlighted ? 'ring-2 ring-blue-500' : ''}`}
        >
            <div className="flex flex-row justify-between p-4">
              <div className="flex flex-col">
                <span className="text-lg font-semibold">Kontolista ({localAccountMapping.accounts.filter((x) => !x.groupId).length})</span>
                {accountFilter && (
                  <div className="flex items-center mt-1">
                    <Checkbox
                      size="small"
                      onChange={handleCheckAllFiltered}
                      checked={
                        getFilteredAccounts()
                          .every(a => selectedAccounts.has(a.number))
                      }
                      indeterminate={
                        getFilteredAccounts()
                          .some(a => selectedAccounts.has(a.number)) &&
                        !getFilteredAccounts()
                          .every(a => selectedAccounts.has(a.number))
                      }
                      disabled={getFilteredAccounts().length === 0}
                    />
                  </div>
                )}
              </div>
              <TextField
                label={"Filtrera på kontonummer"}
                variant="outlined"
                value={accountFilter}
                onChange={(e) => setAccountFilter(e.target.value)}
                className="w-1/2"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {accountFilter && (
                        <IconButton 
                          onClick={() => setAccountFilter("")}
                          edge="end"
                          size="small"
                          aria-label="clear filter"
                        >
                          <X size={16} />
                        </IconButton>
                      )}
                    </InputAdornment>
                  )
                }}
              />
            </div>
            <div ref={(el) => el && setupAccountListDropTarget(el)} className="overflow-y-auto flex-grow px-4 pt-1 -m-2">
              <div className="flex flex-col gap-2">
                {getFilteredAccounts().map((a) => (
                    <div 
                      key={a.number} 
                      ref={(el) => el && setupDragAndDrop(el, a.number)}
                      data-account-number={a.number}
                      onClick={(e) => handleCardClick(a.number, e)}
                      className="cursor-pointer"
                    >
                      <AccountCard 
                        account={a}
                        isSelected={selectedAccounts.has(a.number)}
                        onSelectToggle={handleAccountSelection}
                      />
                    </div>
                  ))}
              </div>
            </div>
        </Card>
        <Card
          hoverable={false}
          className="w-1/2 border border-palette-dark-gray rounded-lg flex flex-col h-full"
        >
          <div className="flex flex-row justify-between p-4">
            <span className="text-lg font-semibold">Kontogrupper</span>
            <IconButton onClick={() => setIsCreateDialogOpen(true)}>
              <Plus />
            </IconButton>
          </div>
          <div className="flex flex-col gap-4 p-4 overflow-y-auto flex-grow">
            {localAccountMapping.groups.map((group) => {
                const groupAccounts = localAccountMapping.accounts
                .filter((x) => x.groupId === group.id);
                return (
                <div 
                  key={group.id} 
                  ref={(el) => el && setupDropTarget(el, group.id)}
                  className={`${highlightedGroupId === group.id ? 'ring-2 ring-blue-500 rounded-2xl' : ''}`}
                >
                  <Card
                    className="flex flex-col gap-2 bg-background-light border border-border"
                  >
                    <div className="flex justify-between items-center">
                      <div 
                        className="flex items-center cursor-pointer flex-grow"
                        onClick={() => toggleGroupExpansion(group.id)}
                      >
                        <span className="text-md font-semibold">{group.name}</span>
                        <span className="ml-2 text-sm text-gray-500">({groupAccounts.length})</span>
                      </div>
                      <div className="flex">
                        <IconButton onClick={() => toggleGroupExpansion(group.id)}>
                          {expandedGroups.has(group.id) ? (
                            <ChevronsDownUp size={16} />
                          ) : (
                            <ChevronsUpDown size={16} />
                          )}
                        </IconButton>
                        <IconButton onClick={() => { setSelectedGroup(group); setIsConfirmDeleteDialogOpen(true); }}>
                          <Trash2 size={16} />
                        </IconButton>
                        <IconButton onClick={() => { setSelectedGroup(group); setIsCreateDialogOpen(true); }}>
                          <SquarePen size={16} />
                        </IconButton>
                      </div>
                    </div>
                    <Divider />
                    <div className={`transition-all duration-300 ease-in-out ${
                      expandedGroups.has(group.id)
                        ? 'max-h-[500px] opacity-100 overflow-y-auto' 
                        : 'max-h-0 opacity-0 overflow-hidden'
                    }`}>
                      {!groupAccounts.length && (
                        <span className="text-sm text-gray-500 italic">
                          Släpp konton här för att lägga till i gruppen
                        </span>)}
                      {groupAccounts
                        .map((a) => (
                          <div 
                            key={a.number} 
                            ref={(el) => el && setupDragAndDrop(el, a.number)}
                            data-account-number={a.number}
                            onClick={(e) => handleCardClick(a.number, e)}
                            className="cursor-pointer"
                          >
                            <AccountCard 
                              account={a}
                              isSelected={selectedAccounts.has(a.number)}
                              onSelectToggle={handleAccountSelection}
                            />
                          </div>
                        ))}
                    </div>
                  </Card>
                </div>
            )})}
          </div>
        </Card>
      </div>
      <CreateEditAccountGroupDialog
        open={isCreateDialogOpen}
        onOpenChange={(x) => {setIsCreateDialogOpen(x); if (x == false) setSelectedGroup(null);}}
        onCreateAccountGroup={handleCreateAccountGroup}
        groupToEdit={selectedGroup}
        onEditAccountGroup={handleEditAccountGroup}
      />
      <ConfirmDialog
        open={isConfirmDeleteDialogOpen}
        onOpenChange={(x) => {setIsConfirmDeleteDialogOpen(x); if (x == false) setSelectedGroup(null);}}
        title="Ta bort kontogrupp"
        text={`Är du säker på att du vill ta bort kontogrupp ${selectedGroup?.name}?`}
        confirmId={selectedGroup?.id || ""}
        onConfirm={handleDeleteAccountGroup}
      />
    </div>
  );
};

export default EditAccountMapping;
